<script setup lang="ts">
import type { Product } from 'types/models/product'
import type { AddToCartSources } from '~/types/gtm'
import type { Story } from '~/types/storyblok'
import type { ProductListMediaCard } from '~/types/storyblok/product-list-media-card'

interface Props {
  products: Product[]
  loading: boolean
  page: number
  pages: number
  isEmpty: boolean
  prevPageUrl: string | null
  nextPageUrl: string | null
  totalProducts: number
  productListMediaCards?: Story<ProductListMediaCard>[]
  pageSource?: AddToCartSources
}
const props = withDefaults(defineProps<Props>(), {
  pageSource: 'categoryPage',
})
const { selectedGridOption } = useProductGridOptions()

const isFirstPage = computed(() => {
  return props.page === 1
})
const isLastPage = computed(() => {
  return props.page === props.pages
})

const smallGrid = computed(() => {
  return selectedGridOption.value.value === 'image-only'
})

const { shouldShowMediaCard, getMediaCardContent } = useProductMediaCards(
  props.productListMediaCards,
  {
    smallGrid,
    isFirstPage,
  },
)
</script>

<template>
  <div>
    <div v-if="loading" class="loading-state" />
    <div v-if="!isEmpty && products?.length">
      <p
        v-if="totalProducts > 0" class="total-products" data-test="total-products"
        :aria-label="`${totalProducts} total products`"
      >
        {{ totalProducts }} {{ $t('products') }}
      </p>

      <div class="product-grid" :class="{ 'product-grid--small': smallGrid }" data-test="plp-product-list">
        <template v-for="product, index in products" :key="product.sku">
          <template v-if="shouldShowMediaCard(index)">
            <ProductListMediaCard
              v-if="getMediaCardContent(index)" class="product-list-media-card"
              :blok="getMediaCardContent(index) "
            />
          </template>
          <ProductCard
            :product="product" :has-quick-shop="true"
            :grid-type="smallGrid ? 'product-grid-small' : undefined"
            :page-source="props.pageSource"
          />
        </template>
      </div>

      <div class="pagination" role="navigation" :aria-label="$t('paginationAria')" data-test="pagination">
        <ButtonIcon
          v-if="!isFirstPage && !loading && prevPageUrl" tag="nuxt-link" :aria-label="$t('loadPreviousPage')"
          data-test="load-previous-button" :border-rounded="false" :to="prevPageUrl" class="cta back" left-icon="back"
          :disabled="loading"
        />

        <p class="pagination-text" data-test="pagination-text" :aria-label="`page ${String(page)}`">
          {{ $t('pagination', { currentPage: String(page), totalPages: String(pages) }) }}
        </p>

        <ButtonIcon
          v-if="!isLastPage && !loading && nextPageUrl" tag="nuxt-link" :aria-label="$t('loadNextPage')"
          data-test="load-more-button" :border-rounded="false" :to="nextPageUrl" class="cta forward"
          right-icon="forward" :disabled="loading"
        />
      </div>
    </div>

    <p v-else-if="!loading" class="nothing-found">
      {{ $t('cantFindProducts') }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';
@import 'assets/scss/typography/body';

.total-products {
  @include body2;
  color: var(--text-secondary);
  padding: 0.8rem 0 1.6rem 1.6rem;

  @media (min-width: $tablet) {
    padding: 1.6rem 0 1.6rem 2.2rem;
  }
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  border-top: 1px solid var(--gray-light);
  border-bottom: 1px solid var(--gray-light);
  background-color: var(--gray-light);
  grid-gap: 1px;

  @media (min-width: $tablet) {
    grid-template-columns: repeat(4, 1fr);
  }

  &--small {
    border: unset;
    background-color: unset;
    grid-template-columns: repeat(auto-fit, minmax(120px, 0.5fr));

    @media (min-width: $phablet) {
      grid-template-columns: repeat(auto-fit, minmax(150px, 0.25fr));
    }
  }
}

.product-list-media-card {
  grid-column: span 2;
}

.loading-state {
  min-height: 100vh;
}

.pagination {
  max-width: 30rem;
  margin: 0 auto;
  padding: 3.2rem 0.5rem 6.4rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-areas: "ctaBack text ctaForward";
  justify-items: center;
}

.cta {
  height: 4rem;
  width: 4rem;
  border-radius: 50%;
  display: flex;
  padding: 0;
  gap: 0;
  justify-content: center;

  &.back {
    grid-area: ctaBack;
    place-self: center start;
  }

  &.forward {
    grid-area: ctaForward;
    place-self: center end;
  }
}

.pagination-text {
  @include body2;
  grid-area: text;
  place-self: center;
  text-wrap: nowrap;
}

.nothing-found {
  margin: 0 2.4rem 4.8rem;
}

section {
  @media (min-width: $tablet) {
    padding: var(--spacing-m);
  }
}
</style>
